import React, {
  createContext,
  PropsWithChildren,
  useCallback,
  useEffect,
  useState,
} from "react";
import { ConnectorState } from "../hooks";
import { Web3ConnectionType, Web3ConnectOptions } from "../types";
import { Connector } from "../connector";
import { Connector as ConnectorType } from "@web3-react/types";

export interface Web3ProviderOptions {
  options: Web3ConnectOptions;
}

export interface Web3ProviderState {
  connectionType: Web3ConnectionType;
  state: ConnectorState;
  desiredChainId?: number;
  stayConnected?: boolean;

  connect(): void;

  disconnect(): void;

  setConnectionType(type: Web3ConnectionType): void;

  setDesiredChainId(id: number | undefined): void;
}

export const Web3Context = createContext<Web3ProviderState>(null!);

export const SESSION_KEY = "web3-connect:connection-type";

export const Web3Provider = ({
  children,
  options,
}: PropsWithChildren<Web3ProviderOptions>) => {
  const [connectionType, setConnectionType] = useState(
    () =>
      getSessionConnection() ||
      options.defaultConnectionType ||
      Web3ConnectionType.NETWORK,
  );
  const [connector, setConnector] = useState<ConnectorType | null>(null);

  const [state, setState] = useState<null | ConnectorState>(null);

  const [desiredChainId, setDesiredChainId] = useState<number | undefined>();

  const connectComponent = (
    <Connector
      type={connectionType}
      onStateUpdate={(state) => setState(state)}
      onConnectorUpdate={(connector) => setConnector(connector)}
      options={options}
      chainId={desiredChainId}
    />
  );

  useEffect(() => {
    if (typeof sessionStorage !== "undefined") {
      sessionStorage.setItem(SESSION_KEY, connectionType);
    }
  }, [connectionType]);

  const setConnectionTypeHandler = useCallback(
    (type: Web3ConnectionType) => {
      if (connectionType === type) {
        connect();
      } else {
        setConnectionType(type);
      }
    },
    [connectionType],
  );

  const connect = useCallback(() => {
    return connector?.activate();
  }, [connector]);

  const disconnect = useCallback(() => {
    connector?.deactivate?.();
  }, [connector]);

  if (!state || !connector) {
    return connectComponent;
  }

  return (
    <Web3Context.Provider
      value={{
        connectionType,
        setConnectionType: setConnectionTypeHandler,
        desiredChainId,
        setDesiredChainId,
        state,
        connect,
        disconnect,
      }}
    >
      {connectComponent}
      {children}
    </Web3Context.Provider>
  );
};

function getSessionConnection() {
  if (typeof sessionStorage !== "undefined") {
    return (
      (sessionStorage.getItem(SESSION_KEY) as Web3ConnectionType) ?? undefined
    );
  }
}
